import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/**
 * Breadcrumb — compact, theme-aware orientation strip.
 *
 * Usage:
 *   <Breadcrumb
 *     items={[["Industries","/industries"],["Lodges","/industries/lodges"]]}
 *     theme="light"|"dark"|"overlay"
 *     testid="breadcrumb"
 *   />
 *
 * `theme="overlay"` is for use over a hero photograph — light text + soft shadow.
 * Last item renders as the current page (non-link, cyan).
 */
export default function Breadcrumb({
  items = [],
  theme = "light",
  testid = "breadcrumb",
  className = "",
}) {
  if (!items || items.length === 0) return null;

  const palette = {
    light:   { ink: "rgba(12,42,64,0.65)", current: "#1FB6C1", sep: "rgba(12,42,64,0.25)" },
    dark:    { ink: "rgba(245,247,250,0.62)", current: "#1FB6C1", sep: "rgba(245,247,250,0.32)" },
    overlay: { ink: "rgba(255,255,255,0.85)", current: "#1FB6C1", sep: "rgba(255,255,255,0.5)" },
  }[theme] || palette?.light || {};

  return (
    <nav
      aria-label="Breadcrumb"
      data-testid={testid}
      className={`font-mono uppercase inline-flex items-center flex-wrap gap-1 ${className}`}
      style={{
        fontSize: "0.62rem",
        letterSpacing: "0.24em",
        fontWeight: 700,
        textShadow: theme === "overlay" ? "0 1px 2px rgba(0,0,0,0.35)" : undefined,
      }}
    >
      {items.map(([name, path], i) => {
        const isLast = i === items.length - 1;
        return (
          <React.Fragment key={`${name}-${i}`}>
            {isLast ? (
              <span
                aria-current="page"
                style={{ color: palette.current }}
                data-testid={`${testid}-current`}
              >
                {name}
              </span>
            ) : (
              <Link
                to={path}
                className="hover:opacity-100 transition-opacity"
                style={{ color: palette.ink, opacity: 0.92 }}
                data-testid={`${testid}-link-${i}`}
              >
                {name}
              </Link>
            )}
            {!isLast && (
              <ChevronRight
                size={11}
                aria-hidden
                style={{ color: palette.sep, opacity: 0.7 }}
              />
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
